import { useState, useEffect } from "react"
import { Plus, FileText } from "lucide-react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { PageHeader } from "@/components/PageHeader"
import { createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"

interface DashboardStats {
  totalAvaliacoes: number
  pendentesAprovacao: number
  concluidosHoje: number
  economiaNaCompra: number
}

interface AvaliacaoRecente {
  id: string
  veiculo_id: string
  status: string
  nota_final?: number
  created_at: string
  reparosCount?: number
  custoEstimado?: number
  veiculos: {
    placa: string
    marca: string
    modelo: string
    ano: number
    cor: string
    foto_principal?: string | null
  }
}

const Dashboard = () => {
  const vertical = useTipoOperacao()
  const [stats, setStats] = useState<DashboardStats>({
    totalAvaliacoes: 0,
    pendentesAprovacao: 0,
    concluidosHoje: 0,
    economiaNaCompra: 0
  })
  const [recentAvaliacoes, setRecentAvaliacoes] = useState<AvaliacaoRecente[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
    fetchRecentAvaliacoes()
  }, [])

  const fetchRecentAvaliacoes = async () => {
    try {
      const { data, error } = await supabase
        .from('avaliacoes')
        .select(`
          *,
          veiculos:veiculo_id (
            placa,
            marca,
            modelo,
            ano,
            cor,
            foto_principal
          )
        `)
        .order('created_at', { ascending: false })
        .limit(8)

      if (error) {
        console.error("Erro ao buscar avaliações recentes:", error)
        return
      }

      const avaliacoesResolvidas = await Promise.all(
        ((data || []) as AvaliacaoRecente[]).map(async (avaliacao) => {
          const fotoPrincipal = avaliacao.veiculos?.foto_principal
          if (!fotoPrincipal) return avaliacao

          return {
            ...avaliacao,
            veiculos: {
              ...avaliacao.veiculos,
              foto_principal: (await createSignedFileUrl(PHOTO_BUCKET, fotoPrincipal)) ?? fotoPrincipal,
            },
          }
        })
      )

      const avaliacoesIds = avaliacoesResolvidas.map((avaliacao) => avaliacao.id)
      const metricasPorAvaliacao: Record<string, { reparosCount: number; custoEstimado: number }> = {}

      if (avaliacoesIds.length > 0) {
        const { data: criteriosData, error: criteriosError } = await supabase
          .from("avaliacoes_criterios")
          .select("avaliacao_id, custo_reparo, status, aprovado")
          .in("avaliacao_id", avaliacoesIds)

        if (criteriosError) {
          console.error("Erro ao carregar métricas das avaliações recentes:", criteriosError)
        } else {
          criteriosData?.forEach((criterio) => {
            const isReprovado = criterio.status
              ? criterio.status === "reprovado"
              : criterio.aprovado === false

            if (!isReprovado) return

            const atual = metricasPorAvaliacao[criterio.avaliacao_id] ?? {
              reparosCount: 0,
              custoEstimado: 0,
            }

            atual.reparosCount += 1
            atual.custoEstimado += criterio.custo_reparo || 0
            metricasPorAvaliacao[criterio.avaliacao_id] = atual
          })
        }
      }

      setRecentAvaliacoes(
        avaliacoesResolvidas.map((avaliacao) => ({
          ...avaliacao,
          reparosCount: metricasPorAvaliacao[avaliacao.id]?.reparosCount ?? 0,
          custoEstimado: metricasPorAvaliacao[avaliacao.id]?.custoEstimado ?? 0,
        }))
      )
    } catch (error) {
      console.error("Erro inesperado ao buscar avaliações recentes:", error)
    }
  }

  const fetchDashboardStats = async () => {
    try {
      // Buscar contagem total de avaliações
      const { count: totalAvaliacoesCount, error: totalAvaliacoesError } = await supabase
        .from('avaliacoes')
        .select('*', { count: 'exact', head: true })

      if (totalAvaliacoesError) throw totalAvaliacoesError

      // Buscar contagem de avaliações pendentes
      const { count: pendentesCount, error: pendentesError } = await supabase
        .from('avaliacoes')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pendente')

      if (pendentesError) throw pendentesError

      // Buscar avaliações concluídas hoje
      const hoje = new Date()
      const inicioDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
      const fimDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 1)

      const { count: concluidosHojeCount, error: concluidosError } = await supabase
        .from('avaliacoes')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'concluida')
        .gte('created_at', inicioDia.toISOString())
        .lt('created_at', fimDia.toISOString())

      if (concluidosError) throw concluidosError

      // Buscar economia nos últimos 30 dias (FIPE - valor ofertado)
      const trintaDiasAtras = new Date()
      trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30)

      const { data: propostasData, error: propostasError } = await supabase
        .from('propostas')
        .select('valor_fipe, valor_ofertado')
        .gte('created_at', trintaDiasAtras.toISOString())
        .gt('valor_fipe', 0)
        .gt('valor_ofertado', 0)

      if (propostasError) throw propostasError

      const economiaNaCompra = (propostasData || [])
        .reduce((acc, p) => acc + (p.valor_fipe - p.valor_ofertado), 0)

      setStats({
        totalAvaliacoes: totalAvaliacoesCount || 0,
        pendentesAprovacao: pendentesCount || 0,
        concluidosHoje: concluidosHojeCount || 0,
        economiaNaCompra: Math.max(0, economiaNaCompra)
      })

    } catch (error) {
      toast.error("Erro ao carregar estatísticas do dashboard")
      console.error("Erro:", error)
    } finally {
      setLoading(false)
    }
  }

  const statsCards = [
    {
      title: vertical.dashboard.totalLabel,
      value: loading ? "..." : stats.totalAvaliacoes.toString(),
      href: undefined,
      desc: undefined,
    },
    {
      title: vertical.tipo === 'oficina' ? "Em Andamento" : "Pendentes Aprovação",
      value: loading ? "..." : stats.pendentesAprovacao.toString(),
      href: "/avaliacoes",
      showLinkText: false,
      desc: undefined,
    },
    {
      title: "Concluídos Hoje",
      value: loading ? "..." : stats.concluidosHoje.toString(),
      href: undefined,
      desc: undefined,
    },
    ...(vertical.dashboard.mostrarCardEconomia
      ? [{
          title: vertical.dashboard.cardPrincipalLabel,
          value: loading ? "..." : stats.economiaNaCompra > 0
            ? stats.economiaNaCompra.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
            : "—",
          href: undefined,
          showLinkText: false,
          desc: undefined,
        }]
      : []),
  ]


  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pendente: "Pendente",
      ativa: "Em Andamento",
      concluida: "Concluída",
      cancelada: "Cancelada"
    }

    // Fallback para status desconhecidos
    const normalizedStatus = status in colors ? status : 'pendente';

    return <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
      normalizedStatus === 'pendente' ? 'bg-secondary text-secondary-foreground' :
      normalizedStatus === 'ativa' ? 'bg-blue-100 text-blue-800' :
      normalizedStatus === 'concluida' ? 'bg-green-100 text-green-800' :
      normalizedStatus === 'cancelada' ? 'bg-red-100 text-red-800' :
      'bg-destructive text-destructive-foreground'
    }`}>
      {colors[normalizedStatus]}
    </span>
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        title={vertical.dashboard.tituloPainel}
        description="Acompanhe registros, fechamentos e o andamento da sua operação"
      />

      {/* Stats Cards */}
      <div className={`grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 ${statsCards.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        {statsCards.map((stat) => {
          const cardContent = (
            <Card
              key={stat.title}
              className={`h-full ${stat.href ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
            >
              <CardContent className="flex h-full min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
                <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">
                  {stat.title}
                </p>
                <div className="mt-2 sm:mt-4">
                  <div className="font-data text-[2rem] font-semibold leading-none text-foreground sm:text-3xl">
                    {stat.value}
                  </div>
                </div>
                <div className="mt-1 min-h-0 sm:mt-2 sm:min-h-4">
                  {stat.desc && (
                    <p className="text-xs text-muted-foreground">{stat.desc}</p>
                  )}
                  {stat.href && stat.showLinkText !== false && (
                    <p className="text-xs text-muted-foreground">Ver detalhes →</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )

          return stat.href ? (
            <Link key={stat.title} to={stat.href} className="block h-full">
              {cardContent}
            </Link>
          ) : (
            <div key={stat.title} className="h-full">{cardContent}</div>
          )
        })}
      </div>

      {/* Recent Evaluations */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>{vertical.dashboard.secaoRecentes}</CardTitle>
            <CardDescription>Últimos registros da sua operação</CardDescription>
          </div>
          <Button asChild size="sm" className="w-full sm:w-auto">
            <Link to="/veiculos">
              <Plus className="mr-2 h-4 w-4" />
              Selecionar veículo
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentAvaliacoes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <div className="p-4 rounded-full bg-primary/10">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-1">
                <p className="font-medium">{`Nenhuma ${vertical.labels.labelPrincipal.toLowerCase()} ainda`}</p>
                <p className="text-sm text-muted-foreground">
                  {`Escolha um veículo para registrar a primeira ${vertical.labels.labelPrincipal.toLowerCase()}`}
                </p>
              </div>
              <Button asChild>
                <Link to="/veiculos">
                  <Plus className="mr-2 h-4 w-4" />
                  Selecionar veículo
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentAvaliacoes.map((avaliacao) => (
                <div
                  key={avaliacao.id}
                  className="rounded-lg border p-3 transition-colors hover:bg-muted/50 sm:p-4"
                >
                  <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1.4fr)_auto_auto] lg:items-center lg:gap-6">
                    <div className="flex min-w-0 gap-3">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border bg-muted/60 sm:h-16 sm:w-16">
                        {avaliacao.veiculos?.foto_principal ? (
                          <img
                            src={avaliacao.veiculos.foto_principal}
                            alt={`Foto do veículo ${avaliacao.veiculos?.placa || avaliacao.id}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                            Sem foto
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <h4 className="text-base font-semibold leading-none">
                            {avaliacao.veiculos?.placa || "Placa não informada"}
                          </h4>
                          {getStatusBadge(avaliacao.status)}
                        </div>
                        <p className="mb-1 text-sm text-muted-foreground">
                          {avaliacao.veiculos?.marca} {avaliacao.veiculos?.modelo} · {avaliacao.veiculos?.ano}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(avaliacao.created_at).toLocaleDateString('pt-BR')}
                          {avaliacao.nota_final && (
                            <span className="ml-2 font-medium">· {avaliacao.nota_final.toFixed(1)}/10</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {vertical.dashboard.mostrarMetricasReparos ? (
                      <div className="grid grid-cols-2 gap-2 sm:max-w-[320px] lg:w-[280px] lg:grid-cols-2">
                        <div className="rounded-xl bg-secondary/70 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Reparos
                          </p>
                          <p className="mt-1 font-data text-lg font-semibold leading-none text-foreground">
                            {avaliacao.reparosCount ?? 0}
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {(avaliacao.reparosCount ?? 0) === 1 ? "item reprovado" : "itens reprovados"}
                          </p>
                        </div>

                        <div className="rounded-xl bg-secondary/70 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Custo
                          </p>
                          <p className="mt-1 font-data text-lg font-semibold leading-none text-foreground">
                            {(avaliacao.custoEstimado ?? 0) > 0
                              ? (avaliacao.custoEstimado ?? 0).toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                  maximumFractionDigits: 0,
                                })
                              : "—"}
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {(avaliacao.custoEstimado ?? 0) > 0 ? "impacto estimado" : "sem custo"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 sm:max-w-[320px] lg:w-[280px] lg:grid-cols-2">
                        <div className="rounded-xl bg-secondary/70 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Pontos críticos
                          </p>
                          <p className="mt-1 font-data text-lg font-semibold leading-none text-foreground">
                            {avaliacao.reparosCount ?? 0}
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {(avaliacao.reparosCount ?? 0) === 1 ? "item documentado" : "itens documentados"}
                          </p>
                        </div>

                        <div className="rounded-xl bg-secondary/70 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Status
                          </p>
                          <p className="mt-1 font-data text-lg font-semibold leading-none text-foreground">
                            {avaliacao.status === "concluida" ? "Pronta" : "Em curso"}
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {avaliacao.status === "concluida"
                              ? "termo pode ser emitido"
                              : "documentação em andamento"}
                          </p>
                        </div>
                      </div>
                    )}

                    <Button variant="outline" size="sm" asChild className="w-full lg:w-auto lg:min-w-[148px]">
                      <Link to={`/avaliacao/${avaliacao.id}`}>
                        Ver Detalhes
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Dashboard
